import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface TagFilterState {
  /** Tag `_id`s the item must have. Resolved from DEFAULT_INCLUDE_TAG_NAMES once tags load. */
  includeTagRefs: string[];
  /**
   * When true, the item must not have ANY tag other than includeTagRefs — a dynamic "only these
   * tags" rule, not an enumerated exclude list (per the ticket: "must not have any OTHER tag",
   * not a fixed set like Specialty/Nurture/Series Prizes). When false, excludeTagRefs below is
   * used as an explicit exclude list instead.
   */
  excludeAllOtherTags: boolean;
  /** Tag `_id`s the item must not have. Only used when excludeAllOtherTags is false. */
  excludeTagRefs: string[];
}

/**
 * Pre-filled the first time the Tags filter loads. There's no filter-persistence service yet
 * (separate ticket) — until saved filters can be loaded, every session starts from this
 * product-specified default: item must have the "Prize" tag, and must not have any other tag.
 */
const DEFAULT_INCLUDE_TAG_NAMES: ReadonlyArray<string> = ['Prize'];

const DEFAULT_TAG_FILTER_STATE: TagFilterState = {
  includeTagRefs: [],
  excludeAllOtherTags: true,
  excludeTagRefs: [],
};

export function getDefaultIncludeTagNames(): string[] {
  return [...DEFAULT_INCLUDE_TAG_NAMES];
}

/**
 * Real state behind the Tags filter section: `state$` is the single source of truth, written to
 * by PrizeTagFilterComponent and read by both the component (to render the include/exclude
 * pickers) and the "must have a Tag filter" submit guard in GeneratePrizesPanelComponent (via
 * hasAtLeastOneRule() below). `excludeAllOtherTags` defaults to true, so the guard is satisfied
 * from the moment the service is created — the component resolves the real "Prize" tag ref into
 * includeTagRefs shortly after, once the tags list has loaded.
 */
@Injectable({
  providedIn: 'root',
})
export class PrizeTagFilterStateService {
  private readonly state$ = new BehaviorSubject<TagFilterState>({ ...DEFAULT_TAG_FILTER_STATE });

  getState(): TagFilterState {
    return this.state$.value;
  }

  watchState(): Observable<TagFilterState> {
    return this.state$.asObservable();
  }

  setState(state: TagFilterState): void {
    this.state$.next(state);
  }

  hasAtLeastOneRule(): boolean {
    const state = this.state$.value;
    return state.includeTagRefs.length > 0 || state.excludeTagRefs.length > 0 || state.excludeAllOtherTags;
  }
}
